import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  PerkPointTransactionApi,
  TransactionReportItem,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";

export function ReportAllTransactions() {
  const [reportRows, setReportRows] = useState<TransactionReportItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const { toast } = useToast();

  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === "0001-01-01T00:00:00") {
      return "-";
    }
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return dateString;
    }
  };

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const response = await PerkPointTransactionApi.getAllTransactions({
        p_PageNumber: currentPage + 1,
        p_PageSize: pageSize,
      });

      setReportRows(
        Array.isArray(response.allTransactions) ? response.allTransactions : []
      );
      setTotalRecords(response.total_records ?? 0);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching transaction report:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch transaction report",
        variant: "destructive",
      });
      setReportRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">Transaction Report</h2>
        <p className="text-sm text-muted-foreground">
          View all loyalty transactions.
        </p>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <DataTable
            title=""
            data={reportRows}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "Mobile No",
                accessorKey: "mobileNo" as keyof TransactionReportItem,
              },
              {
                header: "Invoice No",
                accessorKey: "invoiceNo" as keyof TransactionReportItem,
              },
              {
                header: "Invoice Amount",
                accessorKey: "invoiceAmt" as keyof TransactionReportItem,
                cell: (row: TransactionReportItem) =>
                  row.invoiceAmt.toLocaleString(),
              },
              {
                header: "Transaction Type",
                accessorKey: "transactionType" as keyof TransactionReportItem,
                cell: (row: TransactionReportItem) => (
                  <Badge
                    variant={
                      row.transactionType.toLowerCase() === "earn"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {row.transactionType}
                  </Badge>
                ),
              },
              {
                header: "Transacted Date",
                accessorKey: "transactedDate" as keyof TransactionReportItem,
                cell: (row: TransactionReportItem) =>
                  formatDate(row.transactedDate),
              },
              {
                header: "Counter ID",
                accessorKey: "counterId" as keyof TransactionReportItem,
              },
            ]}
          />

          {totalRecords > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value, 10));
                    setCurrentPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPrevHandler={() =>
                  setCurrentPage((page) => Math.max(0, page - 1))
                }
                onNextHandler={() =>
                  setCurrentPage((page) =>
                    Math.min(totalPages - 1, page + 1)
                  )
                }
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
